import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { getMinhasInscricoes, checkCertificadoElegibilidade, downloadCertificado } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { Download, XCircle, CheckCircle, Loader2, LayoutGrid, List, Clock } from 'lucide-react';

function MeusCertificadosPage() {
    const { participanteId, logout } = useAuth();
    const [inscricoes, setInscricoes] = useState([]);
    const [elegibilidade, setElegibilidade] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(null);
    
    // Estados de Visualização e Persistência
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('certificadosViewMode') || 'grid'); 

    // Efeito para persistir o viewMode no localStorage
    useEffect(() => {
        localStorage.setItem('certificadosViewMode', viewMode);
    }, [viewMode]);

    const fetchCertificadosData = useCallback(async () => {
        if (!participanteId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');

        try {
            const inscricoesData = await getMinhasInscricoes(participanteId);
            setInscricoes(inscricoesData);

            if (inscricoesData.length > 0) {
                const elegibilidadePromises = inscricoesData.map(inscricao =>
                    checkCertificadoElegibilidade(inscricao.id).catch(() => false)
                );
                
                const elegibilidadeResults = await Promise.all(elegibilidadePromises);
                
                const elegibilidadeMap = inscricoesData.reduce((acc, inscricao, index) => {
                    acc[inscricao.id] = elegibilidadeResults[index];
                    return acc;
                }, {});
                setElegibilidade(elegibilidadeMap);
            }
        } catch (err) {
            console.error('Erro ao buscar dados de certificados:', err);
            setError(err.message);
            toast.error(err.message);
            if (err.message.includes('401') || err.message.includes('403')) {
                logout();
            }
        } finally {
            setLoading(false);
        }
    }, [participanteId, logout]);

    useEffect(() => {
        fetchCertificadosData();
    }, [fetchCertificadosData]);

    const handleDownload = async (inscricaoId, eventoNome) => {
        setDownloading(inscricaoId);
        try {
            const downloadToastId = toast.loading("A preparar certificado para download...");
            const pdfBlob = await downloadCertificado(inscricaoId);
            
            const url = window.URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `certificado_${eventoNome.replace(/\s+/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            toast.update(downloadToastId, { render: "Certificado baixado com sucesso!", type: "success", isLoading: false, autoClose: 5000 });
        } catch (err) {
            console.error('Erro no download do certificado:', err);
            toast.error(`Erro: ${err.message}`);
        } finally {
            setDownloading(null);
        }
    };

    const CertificadosSkeleton = () => (
        // Usando gap-6 aqui
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {Array.from({ length: 3 }).map((_, index) => (
                <Card key={index} className={viewMode === 'grid' ? 'h-full' : ''}>
                    <CardHeader>
                        <Skeleton className="h-6 w-1/2 mb-2" />
                        <Skeleton className="h-4 w-1/3 mt-2" />
                    </CardHeader>
                    <CardContent className="flex justify-between items-center">
                        <div className="space-y-2">
                            <Skeleton className="h-5 w-24" />
                            <Skeleton className="h-4 w-32" />
                        </div>
                        <Skeleton className="h-10 w-36" />
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    const CertificadoCard = ({ inscricao, isListMode }) => {
        const isEligible = elegibilidade[inscricao.id];
        const eventoNome = inscricao.evento?.nome || 'Evento Desconhecido';
        const dataInicio = inscricao.evento?.dataInicio;
        const dataFim = inscricao.evento?.dataFim;
        const cargaHoraria = inscricao.evento?.cargaHorariaTotal || 'N/A';

        const baseClasses = "flex flex-col transition-all duration-200 ease-in-out hover:shadow-md";
        const listClasses = "sm:flex-row sm:items-center sm:p-4 sm:space-x-4 hover:shadow-md hover:-translate-y-0";
        const gridClasses = "h-full";

        // Determina a variante do botão
        const buttonVariant = isEligible ? 'default' : 'secondary';
        // Usando bg-gray-400 para desabilitado, mas garantindo que o disabled do Button cuide disso
        const buttonClasses = isEligible ? '' : 'bg-gray-400 hover:bg-gray-500 text-white'; 

        return (
            <Card key={inscricao.id} className={`${baseClasses} ${isListMode ? listClasses : gridClasses}`}>
                {/* Container principal que força o layout flex-col e h-full */}
                <div className={`flex-grow ${isListMode ? 'sm:flex sm:flex-grow sm:items-center' : 'flex flex-col h-full'}`}>
                    
                    {/* HEADER: Ocupa o espaço restante no modo grelha */}
                    <CardHeader className={`flex-grow ${isListMode ? 'sm:flex-grow-0 sm:w-2/3' : ''}`}>
                        <CardTitle className={`mb-1 leading-tight ${isListMode ? 'text-lg' : 'text-xl'}`}>{eventoNome}</CardTitle>
                        <CardDescription className="leading-relaxed text-sm">
                            De {dataInicio ? new Date(dataInicio).toLocaleDateString() : 'N/A'} a {dataFim ? new Date(dataFim).toLocaleDateString() : 'N/A'}
                        </CardDescription>
                        <div className="flex items-center text-xs text-muted-foreground mt-1">
                            <Clock className="mr-1.5 h-3 w-3" />
                            <p>Carga Horária: {cargaHoraria}h</p>
                        </div>
                    </CardHeader>
                    
                    {/* CONTENT/FOOTER: Alinhado à parte inferior no modo grelha */}
                    <CardContent className={`flex flex-col justify-end ${isListMode ? 'sm:flex-row sm:items-center sm:justify-end sm:w-1/3 pt-0 sm:pt-0' : 'pt-0 sm:pt-6'}`}>
                        
                        {/* Status de Elegibilidade */}
                        <div className={`flex items-center ${isListMode ? 'sm:mr-4' : 'mb-4'}`}>
                            {isEligible ? (
                                <div className="flex items-center text-green-600">
                                    <CheckCircle className="mr-2 h-5 w-5" />
                                    <p className="font-medium text-sm">Elegível</p>
                                </div>
                            ) : (
                                <div className="flex items-center text-muted-foreground">
                                    <XCircle className="mr-2 h-5 w-5" />
                                    <p className="text-sm">Ainda não elegível</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Botão de Download */}
                        <div className={`flex flex-col ${isListMode ? 'sm:ml-auto sm:flex-shrink-0' : 'w-full'}`}>
                            <Button
                                onClick={() => handleDownload(inscricao.id, eventoNome)}
                                disabled={downloading === inscricao.id || !isEligible}
                                className={`w-full flex-shrink-0 ${buttonClasses}`}
                                variant={buttonVariant}
                            >
                                {downloading === inscricao.id ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="mr-2 h-4 w-4" />
                                )}
                                {downloading === inscricao.id ? 'A baixar...' : 'Baixar Certificado'}
                            </Button>
                        </div>
                    </CardContent>
                </div>
            </Card>
        );
    };

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Meus Certificados</h1>
                    <p className="text-muted-foreground">Aqui pode encontrar todos os certificados para os eventos em que participou.</p>
                </div>
                {/* Controles de Visualização - Ocultos em telas pequenas (md:flex) */}
                <div className="hidden md:flex space-x-2 flex-shrink-0">
                    <Button 
                        variant={viewMode === 'grid' ? 'default' : 'outline'} 
                        size="icon" 
                        onClick={() => setViewMode('grid')}
                        aria-label="Visualização em Grelha"
                    >
                        <LayoutGrid className="h-5 w-5" />
                    </Button>
                    <Button 
                        variant={viewMode === 'list' ? 'default' : 'outline'} 
                        size="icon" 
                        onClick={() => setViewMode('list')}
                        aria-label="Visualização em Lista"
                    >
                        <List className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {loading ? (
                <CertificadosSkeleton />
            ) : error ? (
                <p className="text-destructive">{error}</p>
            ) : inscricoes.length === 0 ? (
                <Card className="text-center p-8 border-dashed">
                    <CardContent>
                        <p className="text-muted-foreground">Você não tem nenhuma inscrição em eventos.</p>
                    </CardContent>
                </Card>
            ) : (
                // APLICANDO GAP-6 AQUI
                <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                    {inscricoes.map(inscricao => (
                        <CertificadoCard 
                            key={inscricao.id} 
                            inscricao={inscricao} 
                            // Força o modo lista apenas se for desktop E o modo lista estiver ativo
                            isListMode={viewMode === 'list' && window.innerWidth >= 768}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default MeusCertificadosPage;